import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { invoiceApi } from "@/services/api";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Eye,
  Trash2,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  paid: {
    label: "Paid",
    className: "bg-success-light text-success border-success/20",
    icon: CheckCircle2
  },
  pending: {
    label: "Pending",
    className: "bg-warning-light text-warning border-warning/20",
    icon: Clock
  },
  overdue: {
    label: "Overdue",
    className: "bg-destructive-light text-destructive border-destructive/20",
    icon: AlertCircle
  }
};

export default function InvoicesListPage() {
  const navigate = useNavigate();
  
  // State
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, overdue: 0, totalRevenue: 0, pendingRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [pageSize] = useState(10);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await invoiceApi.getInvoices(currentPage, pageSize, searchQuery, statusFilter);
      setInvoices(data.invoices);
      setTotalPages(data.totalPages);
      setTotalInvoices(data.total);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error("Failed to load invoices", {
        description: "Please check your connection and try again."
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, statusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await invoiceApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Seed database if empty
  const seedDatabase = async () => {
    try {
      const result = await invoiceApi.seedInvoices();
      toast.success("Database Seeded", { description: result.message });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error seeding database:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [fetchInvoices, fetchStats]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter]);

  const handleCreateInvoice = async () => {
    try {
      const newInvoice = await invoiceApi.createInvoice();
      toast.success("Invoice Created", {
        description: `New invoice ${newInvoice.id} has been created.`
      });
      navigate(`/invoice/${newInvoice.id}?edit=true`);
    } catch (error) {
      toast.error("Failed to create invoice", {
        description: "Please try again."
      });
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await invoiceApi.deleteInvoice(id);
      toast.success("Invoice Deleted", {
        description: `Invoice ${id} has been deleted.`
      });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete invoice", {
        description: "Please try again."
      });
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoiceApi.updateInvoice(invoiceId, {
        invoiceInfo: { status: newStatus }
      });
      toast.success("Status Updated", {
        description: `Invoice status changed to ${newStatus}.`
      });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update status", {
        description: "Please try again."
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateInvoiceTotal = (lineItems) => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    return subtotal * 1.13; // Include HST
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader onCreateInvoice={handleCreateInvoice} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Invoices</h1>
            <p className="text-muted-foreground mt-1">Manage and track all your car rental invoices</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={seedDatabase} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Seed Data
            </Button>
            <Button onClick={handleCreateInvoice} className="gap-2">
              <Plus className="w-4 h-4" />
              New Invoice
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Total Invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                Paid
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-success">{stats.paid}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-warning" />
                Pending
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-warning">{stats.pending}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Revenue (Paid)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">{formatCurrency(stats.totalRevenue)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by customer name, email, or invoice number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Invoices</CardTitle>
            <CardDescription>
              {totalInvoices} invoice{totalInvoices !== 1 ? 's' : ''} found
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-semibold">Invoice</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold hidden md:table-cell">Date</TableHead>
                    <TableHead className="font-semibold hidden lg:table-cell">Due Date</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          <p className="text-muted-foreground">Loading invoices...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                          <FileText className="w-12 h-12 opacity-30" />
                          <p>No invoices found</p>
                          <Button variant="outline" size="sm" onClick={handleCreateInvoice}>
                            Create your first invoice
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice, index) => {
                      const total = calculateInvoiceTotal(invoice.lineItems);
                      const StatusIcon = statusConfig[invoice.invoiceInfo.status]?.icon || Clock;
                      
                      return (
                        <TableRow
                          key={invoice.id}
                          className={`cursor-pointer transition-colors ${index % 2 === 1 ? 'bg-muted/30' : ''}`}
                          onClick={() => navigate(`/invoice/${invoice.id}`)}
                        >
                          <TableCell className="font-mono font-medium text-primary">
                            #{invoice.id}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-foreground">{invoice.customerInfo.name || 'Unnamed Customer'}</p>
                              <p className="text-xs text-muted-foreground">{invoice.customerInfo.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">
                            {formatDate(invoice.invoiceInfo.date)}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-muted-foreground">
                            {formatDate(invoice.invoiceInfo.dueDate)}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(total)}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={invoice.invoiceInfo.status}
                              onValueChange={(value) => handleStatusChange(invoice.id, value)}
                            >
                              <SelectTrigger className={`w-[120px] h-8 text-xs font-medium ${statusConfig[invoice.invoiceInfo.status]?.className || ''}`}>
                                <div className="flex items-center gap-1.5">
                                  <StatusIcon className="w-3 h-3" />
                                  <SelectValue />
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="paid">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3 h-3 text-success" />
                                    Paid
                                  </div>
                                </SelectItem>
                                <SelectItem value="pending">
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-3 h-3 text-warning" />
                                    Pending
                                  </div>
                                </SelectItem>
                                <SelectItem value="overdue">
                                  <div className="flex items-center gap-2">
                                    <AlertCircle className="w-3 h-3 text-destructive" />
                                    Overdue
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => navigate(`/invoice/${invoice.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete invoice #{invoice.id}? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteInvoice(invoice.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalInvoices)} of {totalInvoices} invoices
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="w-8 h-8 p-0"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
